/**
 * ComplianceReportDialog (POLICY-COMPLIANCE-REPORT-001) — preview of the filtered summary + Excel / PDF download.
 * Summary comes from /policies/compliance/report/preview (same builder as the table) so screen and file agree.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describe = (p) => {
  const bits = [];
  if (p.policy_id) bits.push('Policy filter');
  if (p.team_id) bits.push('Team filter');
  if (p.role) bits.push(`Role: ${p.role}`);
  if (p.status) bits.push(`Status: ${p.status}`);
  if (p.date_from || p.date_to) bits.push(`Assigned ${p.date_from || '…'} → ${p.date_to || '…'}`);
  if (p.q) bits.push(`Search "${p.q}"`);
  return bits.length ? bits.join(' · ') : 'All policies · all teams · all roles · all statuses';
};

export default function ComplianceReportDialog({ open, onOpenChange, params }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    axios.get(`${API}/policies/compliance/report/preview`, { params })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load report preview'));
  }, [open, params]);

  const download = async (format) => {
    setBusy(format);
    try {
      const r = await axios.get(`${API}/policies/compliance/report/export`, { params: { ...params, format }, responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `policy-compliance-report.${format}`;
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format === 'xlsx' ? 'Excel' : 'PDF'} report downloaded`);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? await e.response.data.text().catch(() => '') : e?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail.replace(/^\{"detail":"|"\}$/g, '') : 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  const s = preview?.summary;
  const overCap = preview && s && s.total_assigned > preview.pdf_row_cap;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="compliance-report-dialog">
        <DialogHeader>
          <DialogTitle>Export Policy Compliance Report</DialogTitle>
          <DialogDescription data-testid="compliance-report-scope">Scope: {describe(params)}</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Calculating summary…</div>
        ) : (
          <div className="grid grid-cols-5 gap-2 py-2" data-testid="compliance-report-summary">
            {[['Total Assigned', s.total_assigned], ['Acknowledged', s.acknowledged], ['Pending', s.pending], ['Overdue', s.overdue], ['Ack %', `${s.ack_percentage}%`]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-center">
                <div className="text-base font-bold text-slate-900">{v}</div>
                <div className="text-[10px] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        )}
        {overCap && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="compliance-report-pdf-cap-note">
            PDF detail is limited to the first {preview.pdf_row_cap.toLocaleString()} records — the summary still covers all {s.total_assigned.toLocaleString()}. Use Excel for the complete detailed report.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => download('pdf')} disabled={!preview || !!busy} data-testid="compliance-report-pdf-btn">
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} PDF
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => download('xlsx')} disabled={!preview || !!busy} data-testid="compliance-report-xlsx-btn">
            {busy === 'xlsx' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Excel (.xlsx)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
